import * as React from "react";
import { H } from "highlight.run";
import { authClient } from "@/lib/auth-client";
import type { User, Session } from "@/lib/auth-client";

export interface AuthContext {
	isAuthenticated: boolean;
	login: (email: string, password: string) => Promise<{ data?: any; error?: any }>;
	signup: (email: string, password: string, name: string) => Promise<{ data?: any; error?: any }>;
	logout: () => Promise<void>;
	changePassword: (
		currentPassword: string,
		newPassword: string,
		revokeOtherSessions?: boolean,
	) => Promise<{ data?: any; error?: any }>;
	user: User | undefined;
	isPending: boolean;
	session: Session | undefined;
}

const DEVELOPMENT_CONTEXT: AuthContext = {
	isAuthenticated: true,
	login: async () => ({}),
	signup: async () => ({}),
	logout: async () => {},
	changePassword: async () => ({}),
	user: {
		id: "1",
		email: "admin@example.com",
		name: "Admin User",
		image: "https://example.com/image.png",
		createdAt: new Date(),
		updatedAt: new Date(),
		emailVerified: true,
		banned: false,
		role: "admin",
	},
	isPending: false,
	session: {
		id: "1",
		userId: "1",
		expiresAt: new Date(Date.now() + 1000 * 60 * 60 * 24),
		createdAt: new Date(),
		updatedAt: new Date(),
		token: "1",
		ipAddress: "127.0.0.1",
		userAgent:
			"Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/91.0.4472.124 Safari/537.36",
	},
};

const AuthContext = React.createContext<AuthContext | undefined>(undefined);

/**
 * Promise wrapper around `indexedDB.deleteDatabase`, which hands back an
 * `IDBRequest` rather than a promise — `await`ing the raw request returns
 * immediately and the caller reloads before anything is actually deleted.
 *
 * `blocked` fires when another tab still holds the database open. Resolving
 * there rather than hanging keeps sign-out responsive; the deletion completes
 * once that tab lets go.
 */
function deleteDatabase(name: string): Promise<void> {
	return new Promise((resolve) => {
		const request = indexedDB.deleteDatabase(name);
		request.onsuccess = () => resolve();
		request.onerror = () => resolve();
		request.onblocked = () => resolve();
	});
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
	const { data, isPending } = authClient.useSession();
	const isAuthenticated = !!data?.user;
	const user = data?.user;
	const session = data?.session;

	/**
	 * Signs out and leaves nothing of this account behind in the browser.
	 *
	 * Clearing storage matters because everything this app keeps there is
	 * account-scoped, not device-scoped: the khoa filter on /statistics and the
	 * per-MSSV document checkboxes both describe what the previous user was
	 * working on. Signing in as somebody else would otherwise inherit them.
	 * `clear()` also takes the device preferences (theme, autoScreenshot) with
	 * it — a deliberate trade for the guarantee that nothing survives.
	 *
	 * The reload is the other half. Dropping storage does not touch React state
	 * or any in-memory cache, and Better Auth's session store belongs to the
	 * client instance that owns it, so a fresh document is the only way to be
	 * sure the next sign-in starts from zero rather than from whatever the last
	 * one left in memory. It also picks up a newer bundle after a deploy.
	 */
	const logout = React.useCallback(async () => {
		await authClient.signOut();

		// Before the wipe, not after: highlight.run keeps recording and keeps
		// re-persisting whoever `H.identify` was last called with, so a still-live
		// recorder would put the identity straight back into the storage this
		// function just emptied.
		H.stop();

		localStorage.clear();
		sessionStorage.clear();

		// Nothing registers a service worker today, so this is empty in practice.
		// It is here so that adding one later cannot quietly start leaking one
		// account's responses into the next account's session.
		if ("caches" in window) {
			const keys = await caches.keys();
			await Promise.all(keys.map((key) => caches.delete(key)));
		}

		// Not hypothetical: highlight.run keeps its own copy of whoever `H.identify`
		// was last called with, and restores `highlightUserObject` — full id, email
		// and name — on the next page load even after localStorage is emptied. Left
		// alone, the next person to sign in on this browser gets their session
		// recordings tagged with the previous account.
		// `databases()` is typed as always present but is genuinely missing on
		// older Firefox, hence the runtime probe rather than an optional call.
		if (typeof indexedDB !== "undefined" && "databases" in indexedDB) {
			const dbs = await indexedDB.databases();
			await Promise.all(dbs.map(({ name }) => name && deleteDatabase(name)));
		}

		// Full load, not react-router: see above.
		window.location.replace("/auth/login");
	}, []);

	const login = React.useCallback(async (email: string, password: string) => {
		return await authClient.signIn.email({
			email,
			password,
		});
	}, []);

	const signup = React.useCallback(async (email: string, password: string, name: string) => {
		return await authClient.signUp.email({
			email,
			password,
			name,
		});
	}, []);

	const changePassword = React.useCallback(
		async (currentPassword: string, newPassword: string, revokeOtherSessions: boolean = true) => {
			return await authClient.changePassword({
				currentPassword,
				newPassword,
				revokeOtherSessions,
			});
		},
		[],
	);

	return (
		<AuthContext.Provider
			value={
				import.meta.env.VITE_MOCK_DATA === "true"
					? DEVELOPMENT_CONTEXT
					: {
							isAuthenticated,
							user,
							login,
							signup,
							logout,
							changePassword,
							isPending,
							session,
						}
			}
		>
			{children}
		</AuthContext.Provider>
	);
}

export function useAuth() {
	const context = React.useContext(AuthContext);
	if (!context) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
}
